/**
 * Moving a card between columns, by pointer and by keyboard. CRM-BUILD item 28.
 *
 * ┌─ ⛔ A DRAG WITH NO KEYBOARD PATH IS A BOARD A KEYBOARD USER CANNOT USE ─────┐
 * │ Not "awkward for" - **cannot use**. Dragging is the only way to change a    │
 * │ stage on this surface, so the mouse affordance and the keyboard one are two │
 * │ interfaces to ONE operation and they ship together or neither ships.       │
 * │                                                                            │
 * │ ⚠️ SO THE HOOK OWNS THE OPERATION AND NEITHER INPUT OWNS IT. Both call      │
 * │ `commit`, and the loss-reason question, the optimistic move and the         │
 * │ roll-back happen once rather than once per input method.                   │
 * └────────────────────────────────────────────────────────────────────────────┘
 *
 * ⛔ THE KEYBOARD MODEL IS PICK UP, MOVE, DROP - the column-reorder shape Amir
 * ruled on, and it is the one that works without a pointer position: Enter or
 * Space picks a card up, Left and Right carry it between columns, Enter or Space
 * puts it down, Escape gives up. **Every step is announced**, because a card
 * being carried is a state with no visual equivalent for somebody who cannot see
 * the board.
 */
import { useCallback, useRef, useState } from 'react'
import { moveBooking, type MoveOutcome } from '../data/bookings-api.js'

export type CarriedCard = {
  bookingId: string
  reference: number
  title: string
  /** The column it started in, so Escape and a refusal both know where home is. */
  fromStageId: string
  /** Where it is hovering now, which is the column it lands in on drop. */
  overStageId: string
}

export type PendingConfirm = {
  bookingId: string
  stageId: string
  title: string
  lossReason: string | null
}

export type CardMove = {
  carried: CarriedCard | null
  /** ⛔ The card the board should draw somewhere other than its own column, so
   *  an optimistic move is visible before the server has answered. */
  optimistic: { bookingId: string; stageId: string } | null
  confirm: PendingConfirm | null
  /** ⛔ WHAT A SCREEN READER IS TOLD. Pick-up, each column crossed, the drop and
   *  every refusal. An `aria-live` region renders it. */
  announcement: string
  pickUp: (card: CarriedCard) => void
  /** ⚠️ A DIRECTION, NOT A TARGET. The keyboard path has no pointer position, so
   *  "one column left" is the only thing an arrow key can mean. */
  carryTo: (direction: 'left' | 'right') => void
  drop: () => void
  cancel: () => void
  /** The pointer path: it sets the same state the keyboard path does. */
  dragOver: (stageId: string) => void
  dropOn: (card: CarriedCard, stageId: string) => void
  resolveConfirm: (clear: boolean) => void
}

export function useCardMove(options: {
  /** Column ids in the order they are drawn, so Left and Right mean what they
   *  look like. */
  stageOrder: string[]
  stageLabel: (id: string) => string
  /** ⚠️ Called after any write that changed something, so the board refetches.
   *  NOT called for an unchanged drop: nothing was written. */
  onChanged: () => void
}): CardMove {
  const [carried, setCarried] = useState<CarriedCard | null>(null)
  const [optimistic, setOptimistic] = useState<{ bookingId: string; stageId: string } | null>(null)
  const [confirm, setConfirm] = useState<PendingConfirm | null>(null)
  const [announcement, setAnnouncement] = useState('')
  const { stageOrder, stageLabel, onChanged } = options

  /** ⚠️ A REF RATHER THAN STATE for the in-flight guard: two drops racing would
   *  otherwise both read the same `false` before either had set it. */
  const inFlight = useRef(false)

  const commit = useCallback(
    async (bookingId: string, stageId: string, title: string, clear: boolean) => {
      if (inFlight.current) return
      inFlight.current = true
      /** ⛔ OPTIMISTIC: the card is drawn in its new column immediately, because
       *  a drop that visibly does nothing for 300ms reads as a drop that failed
       *  and gets repeated. */
      setOptimistic({ bookingId, stageId })
      let outcome: MoveOutcome
      try {
        outcome = await moveBooking(bookingId, stageId, clear)
      } catch {
        outcome = { kind: 'failed', message: 'The move could not be saved.' }
      }
      inFlight.current = false

      if (outcome.kind === 'moved') {
        setAnnouncement(`${title} moved to ${stageLabel(stageId)}.`)
        setOptimistic(null)
        onChanged()
        return
      }
      /**
       * ⛔ UNCHANGED IS A SUCCESS AND THE CARD DOES NOT SNAP BACK, because it
       * never went anywhere: this is a drop onto its own column, which
       * `moveBookingStage` deliberately does not write so the activity feed does
       * not fill with transitions that did not happen.
       */
      if (outcome.kind === 'unchanged') {
        setAnnouncement(`${title} is already in ${stageLabel(stageId)}.`)
        setOptimistic(null)
        return
      }
      /**
       * ⛔ THE ONE REFUSAL, AND IT IS A QUESTION. The booking is leaving a lost
       * stage while it still carries a reason, and 0026 refuses rather than
       * clearing it silently: *dropping the reason without being asked destroys
       * the only record of why.*
       *
       * ⚠️ THE OPTIMISTIC MOVE IS ROLLED BACK FIRST, so the card is home while
       * the question is on screen. A card sitting in the new column behind a
       * dialog asserts the move already happened.
       */
      setOptimistic(null)
      if (outcome.kind === 'needs-confirm') {
        setConfirm({ bookingId, stageId, title, lossReason: outcome.lossReason })
        setAnnouncement(`${title} needs confirmation before it can leave the lost stage.`)
        return
      }
      /** ⛔ ANYTHING ELSE SNAPS BACK WITH THE REASON. A card that stays where it
       *  was dropped while the database disagrees is a board showing the wrong
       *  answer, and the next person to load it sees something different. */
      setAnnouncement(`${title} could not be moved. ${outcome.message}`)
    },
    [onChanged, stageLabel],
  )

  const pickUp = useCallback(
    (card: CarriedCard) => {
      setCarried(card)
      setAnnouncement(
        `${card.title} picked up, in ${stageLabel(card.fromStageId)}. ` +
          'Use the left and right arrow keys to choose a stage, Enter to drop, Escape to cancel.',
      )
    },
    [stageLabel],
  )

  /**
   * ⛔ THESE READ `carried` DIRECTLY RATHER THAN THROUGH A `setCarried` UPDATER,
   * AND A BROWSER TEST IS WHY. The first version put `setAnnouncement` and
   * `commit` INSIDE the updater passed to `setCarried`, which reads as a way to
   * get the current value without a dependency - and **React does not promise an
   * updater runs once, or at a point where another setState takes effect.**
   * Under StrictMode it runs twice. The arrow key announced nothing and the card
   * did not move, with no error anywhere.
   *
   * ⚠️ A SIDE EFFECT INSIDE AN UPDATER IS THE DEFECT, not the nesting. The
   * updater's contract is that it is a pure function of the previous state.
   *
   * ⚠️ CLAMPED RATHER THAN WRAPPED. Arrowing past the last column and reappearing
   * at the first is a card that teleports, and a person holding the key down
   * would cycle without noticing they had passed the end.
   */
  const carryTo = useCallback(
    (direction: 'left' | 'right') => {
      if (carried === null) return
      const at = stageOrder.indexOf(carried.overStageId)
      const next =
        direction === 'left' ? Math.max(0, at - 1) : Math.min(stageOrder.length - 1, at + 1)
      const stageId = stageOrder[next] ?? carried.overStageId
      /**
       * ⛔ THE EDGE IS ANNOUNCED RATHER THAN SILENT. A key that does nothing and
       * says nothing is indistinguishable from a key that is not being received,
       * and on a ONE-COLUMN board - which this corpus has, deliberately - every
       * arrow press is this case.
       */
      if (stageId === carried.overStageId) {
        setAnnouncement(
          stageOrder.length === 1
            ? `${stageLabel(stageId)} is the only stage on this board.`
            : `Already at the ${direction === 'left' ? 'first' : 'last'} stage.`,
        )
        return
      }
      setCarried({ ...carried, overStageId: stageId })
      setAnnouncement(`Over ${stageLabel(stageId)}.`)
    },
    [carried, stageOrder, stageLabel],
  )

  const drop = useCallback(() => {
    if (carried === null) return
    setCarried(null)
    void commit(carried.bookingId, carried.overStageId, carried.title, false)
  }, [carried, commit])

  const cancel = useCallback(() => {
    if (carried !== null) {
      setCarried(null)
      setAnnouncement(`${carried.title} put back in ${stageLabel(carried.fromStageId)}.`)
    }
    setConfirm(null)
  }, [carried, stageLabel])

  /** ⚠️ THIS ONE MAY KEEP ITS UPDATER: it is a pure function of the previous
   *  state with no side effect, which is what an updater is for. */
  const dragOver = useCallback((stageId: string) => {
    setCarried((c) => (c === null || c.overStageId === stageId ? c : { ...c, overStageId: stageId }))
  }, [])

  const dropOn = useCallback(
    (card: CarriedCard, stageId: string) => {
      setCarried(null)
      void commit(card.bookingId, stageId, card.title, false)
    },
    [commit],
  )

  /**
   * ⛔ THE CONFIRM'S TWO ANSWERS. Yes re-sends the SAME move with
   * `clearLossReason`, which is the flag `moveBookingStage` takes for exactly
   * this. No leaves the booking lost, with its reason intact.
   */
  const resolveConfirm = useCallback(
    (clear: boolean) => {
      if (confirm === null) return
      setConfirm(null)
      if (clear) void commit(confirm.bookingId, confirm.stageId, confirm.title, true)
      else setAnnouncement(`${confirm.title} stays in its lost stage.`)
    },
    [confirm, commit],
  )

  return {
    carried,
    optimistic,
    confirm,
    announcement,
    pickUp,
    carryTo,
    drop,
    cancel,
    dragOver,
    dropOn,
    resolveConfirm,
  }
}
